import { useEffect, useRef, useState, useCallback } from 'react';

interface UseInactivityTimeoutOptions {
  /** Total time before auto-logout (ms) */
  timeoutMs: number;
  /** Time before timeout to show warning (ms) */
  warningMs: number;
  /** Called when the full timeout elapses */
  onTimeout: () => void;
  /** Whether the hook is active */
  enabled: boolean;
}

export function useInactivityTimeout({
  timeoutMs,
  warningMs,
  onTimeout,
  enabled,
}: UseInactivityTimeoutOptions) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const countdownRef = useRef<ReturnType<typeof setInterval>>();
  const showWarningRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);

  // Keep the callback ref current without re-running effects
  onTimeoutRef.current = onTimeout;

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = undefined;
    logoutTimerRef.current = undefined;
    countdownRef.current = undefined;
  }, []);

  const startTimers = useCallback(() => {
    clearAllTimers();
    lastActivityRef.current = Date.now();
    showWarningRef.current = false;
    setShowWarning(false);

    const warningDelay = timeoutMs - warningMs;

    warningTimerRef.current = setTimeout(() => {
      showWarningRef.current = true;
      setShowWarning(true);
      setSecondsLeft(Math.ceil(warningMs / 1000));

      countdownRef.current = setInterval(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        const left = Math.ceil((timeoutMs - elapsed) / 1000);
        setSecondsLeft(Math.max(0, left));
      }, 1000);
    }, warningDelay);

    logoutTimerRef.current = setTimeout(() => {
      clearAllTimers();
      showWarningRef.current = false;
      setShowWarning(false);
      onTimeoutRef.current();
    }, timeoutMs);
  }, [timeoutMs, warningMs, clearAllTimers]);

  const dismissWarning = useCallback(() => {
    startTimers();
  }, [startTimers]);

  // Set up event listeners once (stable reference via ref check)
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      showWarningRef.current = false;
      setShowWarning(false);
      return;
    }

    startTimers();

    const handleActivity = () => {
      // Only reset if the warning overlay is NOT showing
      if (!showWarningRef.current) {
        startTimers();
      }
    };

    const events: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'wheel',
    ];

    events.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );

    return () => {
      clearAllTimers();
      events.forEach((evt) => window.removeEventListener(evt, handleActivity));
    };
  }, [enabled, startTimers, clearAllTimers]);

  return { showWarning, secondsLeft, dismissWarning };
}
